export const WORLD_MAGIC = "VOXCITY";
export const WORLD_SCHEMA_VERSION = 1;
export const ROAD_SCHEMA_VERSION = 2;

export const ROAD_DIRECTION = {
  NV: 0,
  FT: 1,
  TF: 2,
  TW: 3,
} as const;

export type RoadDirection = (typeof ROAD_DIRECTION)[keyof typeof ROAD_DIRECTION];

export interface WorldSource {
  id: string;
  name: string;
  url: string;
  sha256: string;
  license: string;
}

export interface ChunkIndexEntry {
  id: string;
  lod: 0 | 1;
  x: number;
  z: number;
  url: string;
  byteLength: number;
}
export interface MapTileIndexEntry {
  id: string;
  x: number;
  z: number;
  url: string;
  byteLength: number;
}
export interface DecodedMapTile {
  id: string;
  buildings: Float32Array;
  footprintOffsets: Uint32Array;
  footprints: Float32Array;
  roads: Float32Array;
  roadAttributes: Uint8Array;
  roadIds: Uint32Array;
}

export interface WorldManifest {
  magic: typeof WORLD_MAGIC;
  schemaVersion: typeof WORLD_SCHEMA_VERSION;
  horizontalCrs: string;
  verticalDatum: "NAVD88 metres";
  origin: { latitude: number; longitude: number };
  originElevationM: number;
  chunkSizeM: 128;
  bounds: [number, number, number, number];
  chunkIndex: ChunkIndexEntry[];
  sources: WorldSource[];
  skylineAsset: string;
  groundAsset: string;
  mapTileSizeM: number;
  mapTileIndex: MapTileIndexEntry[];
}

export interface WorldChunk {
  magic: typeof WORLD_MAGIC;
  schemaVersion: typeof WORLD_SCHEMA_VERSION;
  id: string;
  lod: 0 | 1;
  x: number;
  z: number;
  surfaces: Float32Array;
  buildings: Float32Array;
  walkGraph: Uint32Array;
  navmesh: Uint8Array;
  laneGraph: Uint32Array;
  signals: Uint32Array;
  transit: Uint32Array;
}

export function decodeFloat32(bytes: Uint8Array): Float32Array {
  if (bytes.byteLength % Float32Array.BYTES_PER_ELEMENT !== 0)
    throw new Error("Float32 payload has an invalid byte length");
  const copy = Uint8Array.from(bytes);
  return new Float32Array(copy.buffer, 0, copy.byteLength / Float32Array.BYTES_PER_ELEMENT);
}

export function decodeUint32(bytes: Uint8Array): Uint32Array {
  if (bytes.byteLength % Uint32Array.BYTES_PER_ELEMENT !== 0)
    throw new Error("Uint32 payload has an invalid byte length");
  const copy = Uint8Array.from(bytes);
  return new Uint32Array(copy.buffer, 0, copy.byteLength / Uint32Array.BYTES_PER_ELEMENT);
}
export function decodeMapTilePayload(value: unknown, expectedId: string): DecodedMapTile {
  if (!value || typeof value !== "object")
    throw new Error(`Map tile ${expectedId} schema is invalid`);
  const raw = value as {
    magic?: unknown;
    schemaVersion?: unknown;
    roadSchemaVersion?: unknown;
    id?: unknown;
    buildings?: unknown;
    footprintOffsets?: unknown;
    footprints?: unknown;
    roads?: unknown;
    roadAttributes?: unknown;
    roadIds?: unknown;
  };
  if (
    raw.magic !== WORLD_MAGIC ||
    raw.schemaVersion !== WORLD_SCHEMA_VERSION ||
    raw.roadSchemaVersion !== ROAD_SCHEMA_VERSION ||
    raw.id !== expectedId ||
    !(raw.buildings instanceof Uint8Array) ||
    !(raw.footprintOffsets instanceof Uint8Array) ||
    !(raw.footprints instanceof Uint8Array) ||
    !(raw.roads instanceof Uint8Array) ||
    !(raw.roadAttributes instanceof Uint8Array) ||
    !(raw.roadIds instanceof Uint8Array)
  )
    throw new Error(`Map tile ${expectedId} schema is invalid`);
  const buildings = decodeFloat32(raw.buildings);
  const footprintOffsets = decodeUint32(raw.footprintOffsets);
  const footprints = decodeFloat32(raw.footprints);
  const roads = decodeFloat32(raw.roads);
  const roadAttributes = Uint8Array.from(raw.roadAttributes);
  const roadIds = decodeUint32(raw.roadIds);
  const buildingCount = buildings.length / 5;
  const roadCount = roads.length / 5;
  if (
    buildings.length % 5 !== 0 ||
    footprints.length % 2 !== 0 ||
    footprintOffsets.length !== buildingCount + 1 ||
    roads.length % 5 !== 0 ||
    roadAttributes.length !== roadCount * 7 ||
    roadIds.length !== roadCount * 2
  )
    throw new Error(`Map tile ${expectedId} record length is invalid`);
  return {
    id: expectedId,
    buildings,
    footprintOffsets,
    footprints,
    roads,
    roadAttributes,
    roadIds,
  };
}

export function validateManifest(value: unknown): asserts value is WorldManifest {
  if (!value || typeof value !== "object") throw new Error("World manifest is not an object");
  const manifest = value as Partial<WorldManifest>;
  if (manifest.magic !== WORLD_MAGIC) throw new Error("Invalid world manifest magic");
  if (manifest.schemaVersion !== WORLD_SCHEMA_VERSION)
    throw new Error("Unsupported world schema version");
  if (
    manifest.horizontalCrs?.includes("+zone=18") !== true ||
    manifest.verticalDatum !== "NAVD88 metres"
  ) {
    throw new Error("World coordinate reference is invalid");
  }
  if (!Array.isArray(manifest.chunkIndex) || manifest.chunkIndex.length === 0)
    throw new Error("World has no chunks");
  const ids = new Set<string>();
  for (const chunk of manifest.chunkIndex) {
    if (ids.has(`${chunk.lod}:${chunk.id}`)) throw new Error(`Duplicate chunk ${chunk.id}`);
    if (chunk.byteLength <= 0 || (chunk.lod !== 0 && chunk.lod !== 1))
      throw new Error(`Invalid chunk ${chunk.id}`);
    ids.add(`${chunk.lod}:${chunk.id}`);
  }
  if (
    !Number.isFinite(manifest.mapTileSizeM) ||
    (manifest.mapTileSizeM ?? 0) <= 0 ||
    !Array.isArray(manifest.mapTileIndex) ||
    manifest.mapTileIndex.length === 0
  )
    throw new Error("World has no map tiles");
  const mapTileIds = new Set<string>();
  for (const tile of manifest.mapTileIndex) {
    if (mapTileIds.has(tile.id) || tile.byteLength <= 0)
      throw new Error(`Invalid map tile ${tile.id}`);
    mapTileIds.add(tile.id);
  }
}
